import Analytics from '@/utils/analytics';
import {
  Employees_Set_Input,
  SyncUserData_UpdateEmployeeMutation,
  SyncUserData_UpdateEmployeeMutationVariables,
  UserContextUserFragment,
} from '@/types';
import { FocusSource, useComponentFocus } from './use-component-focus';
import { formatLocale } from '@/utils/translations';
import { getLocale } from '@/utils/i18n';
import {
  getLocalUserUpdatesFromEmployee,
  LocalUserUpdates,
} from '@/contexts/user-context';
import { gql, useMutation } from '@apollo/client';
import { partnerSupplier } from '@/utils/environment';
import { useCallback, useEffect } from 'react';
import { USER_FRAGMENT } from '@/contexts/user-context-queries';

const LOCALE_REGEX = /^[a-z]{2}(-.+)?$/;

export const SYNC_USER_DATA_MUTATION = gql`
  mutation SyncUserData_UpdateEmployee(
    $businessOrganizationUUID: uuid!
    $userUUID: uuid!
    $set: employees_set_input
  ) {
    update_employees_by_pk(pk_columns: { uuid: $userUUID }, _set: $set) {
      ...UserContextUser
    }
  }

  ${USER_FRAGMENT}
`;

// Include updated values in the mutation result selection set above
const getUpdateVariables = (user: UserContextUserFragment) => {
  const result: Employees_Set_Input = {};

  const locale = getLocale();
  const formattedDeviceLocale = formatLocale(locale).toLowerCase();
  const formattedUserLocale = formatLocale(user.locale).toLowerCase();
  const userLanguageCode = formattedUserLocale.split('-')[0];

  /*
   * This update results in database issues if employees modified here are
   * locked by reaper bundles. As a temporary workaround to limit the number
   * of mutations made, only update locale if:
   * - saved locale is a different language than the device language
   * - saved locale is non-US English, and would be overridden by en-US
   * TODO: Revert these changes once database issues are resolved.
   */

  const preserveEnglishLocale =
    userLanguageCode === 'en' && formattedDeviceLocale === 'en-us';

  if (!preserveEnglishLocale && formattedUserLocale !== formattedDeviceLocale) {
    if (LOCALE_REGEX.test(formattedDeviceLocale)) {
      result.locale = formattedDeviceLocale;
    } else {
      Analytics.track('user.invalid_locale', { locale: formattedDeviceLocale });
    }
  }

  return result;
};

export const useSyncUserData = (
  user: UserContextUserFragment | null,
  selectedSupplierUUID: string | null,
  updateUserLocally: (updates: LocalUserUpdates) => void,
) => {
  const [updateEmployee, { loading }] = useMutation<
    SyncUserData_UpdateEmployeeMutation,
    SyncUserData_UpdateEmployeeMutationVariables
  >(SYNC_USER_DATA_MUTATION);

  const maybeSyncUserData = useCallback(async () => {
    if (loading || !user) {
      return;
    }

    const updateVariables = getUpdateVariables(user);
    if (!Object.keys(updateVariables).length) {
      return;
    }

    try {
      const result = await updateEmployee({
        variables: {
          businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
          set: updateVariables,
          userUUID: user.uuid,
        },
      });
      const updatedEmployee = result.data?.update_employees_by_pk;
      if (updatedEmployee) {
        const override = updatedEmployee.supplier_employee_overrides?.find(
          (seo) => seo.supplier_uuid === selectedSupplierUUID,
        );
        updateUserLocally({
          ...getLocalUserUpdatesFromEmployee(updatedEmployee, override),
          locale: updatedEmployee.locale,
        });
      }
    } catch (e) {
      // Do nothing
    }
  }, [loading, selectedSupplierUUID, updateEmployee, updateUserLocally, user]);

  useComponentFocus({
    onFocus: useCallback(
      (focusSource: FocusSource) => {
        if (focusSource === FocusSource.App) {
          maybeSyncUserData();
        }
      },
      [maybeSyncUserData],
    ),
  });

  useEffect(() => {
    maybeSyncUserData();
    // Sync when `user` changes, not `maybeSyncUserData`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
};
